import React from 'react';

import { Box, Stack } from '@atlaskit/primitives/compiled';
import { ApplicationEdition } from '@atlassian/invite-people/types';
import { graphql, useFragment, type PreloadedQuery } from 'react-relay';

import { JiraRoleDetail, JiraSpaceOption } from '../../types/SpaceInputTypes';

import { RolePicker } from './RolePicker';
import { SpacePicker } from './SpacePicker';
import type { RolePickerWithPermissionQuery } from './__generated__/RolePickerWithPermissionQuery.graphql';
import type { SpaceInviteInput_data$key } from './__generated__/SpaceInviteInput_data.graphql';

export type SpaceInviteInputProps = {
	selectedSpace: JiraSpaceOption | undefined;
	onSelectSpace: (space: JiraSpaceOption | undefined) => void;
	selectedRole: JiraRoleDetail | undefined;
	setSelectedRole: (role: JiraRoleDetail | undefined) => void;
	appEdition: ApplicationEdition;
	cloudId: string;
	permissionAndRolesQueryRef: PreloadedQuery<RolePickerWithPermissionQuery> | null;
	queryRef: SpaceInviteInput_data$key;
};

export const SpaceInviteInput = ({
	selectedSpace,
	onSelectSpace,
	selectedRole,
	setSelectedRole,
	appEdition,
	cloudId,
	permissionAndRolesQueryRef,
	queryRef,
}: SpaceInviteInputProps) => {
	const data = useFragment(
		graphql`
			fragment SpaceInviteInput_data on Query
			@argumentDefinitions(cloudId: { type: "ID!" }, userId: { type: "String!" }, tenantId: { type: "String!" }) {
				...SpacePicker_data @arguments(cloudId: $cloudId, userId: $userId, tenantId: $tenantId)
				...RolePicker_data @arguments(cloudId: $cloudId)
			}
		`,
		queryRef,
	);

	return (
		<Stack space="space.200">
			<Box>
				<SpacePicker
					selectedSpace={selectedSpace}
					onSelectSpace={onSelectSpace}
					data={data}
					cloudId={cloudId}
					appEdition={appEdition}
				/>
			</Box>

			{selectedSpace && (
				<RolePicker
					permissionAndRolesQueryRef={permissionAndRolesQueryRef}
					setSelectedRole={setSelectedRole}
					selectedRole={selectedRole}
					appEdition={appEdition}
					selectedSpace={selectedSpace}
					queryRef={data}
				/>
			)}
		</Stack>
	);
};
